/**
 * Zeron JSON data model, mirroring `crates/proto/src/entities.rs` and
 * `crates/doc/src/schema.rs`. Every field is camelCase on the wire, and new
 * fields are additive — the browser must tolerate fields it has never heard of
 * (it does: it only reads what it needs) and must never assume an optional
 * field is present, because it may be talking to an older engine.
 *
 * Dates arrive as RFC3339 strings from `DateTime<Utc>` and as epoch millis from
 * the doc layer. Both are normalized to epoch millis here.
 */

export interface Chat {
  id: string;
  deviceId: string;
  title: string | null;
  archived: boolean;
  cwd: string | null;
  branch: string | null;
  checkoutId: string | null;
  config: ChatConfig | null;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  spaceId?: string | null;
  lastSeenAt?: string | null;
  /** `undefined`/1 = legacy room, 2 = chat2. The host flips it at cutover. */
  roomGen?: number | null;
  parentChatId?: string | null;
}

export interface ChatConfig {
  harness: string;
  model: string | null;
  reasoning: string | null;
  modelOptions?: Record<string, unknown>;
  sandbox: SandboxLevel;
}

export type SandboxLevel = "readOnly" | "workspaceWrite" | "dangerFullAccess";

/**
 * A device as the BFF's `/api/devices` reports it, read from the account
 * registry.
 *
 * Deliberately not the RPC `Device` type: that one describes what a *connected*
 * device answers over `WatchDevices`, where timestamps are ISO strings. The
 * registry stores epoch milliseconds. Keeping them separate means neither has to
 * pretend to be the other, and the picker can sort on a number.
 */
export interface RegistryDevice {
  id: string;
  name?: string;
  platform?: string;
  version?: string;
  /** Epoch ms; absent when the device never stamped a boot/shutdown. */
  lastSeenAt?: number;
}

export interface Device {
  id: string;
  name: string;
  platform: string;
  lastSeenAt: string | null;
  createdAt: string;
  version?: string | null;
  capabilities?: string[];
}

/**
 * Convert an RPC `Device` (from a connected device's `WatchDevices`) into the
 * registry shape the picker uses.
 *
 * Both sources feed the same list, and they disagree on timestamps: the RPC
 * sends ISO strings, the registry stores epoch milliseconds. Normalizing at this
 * boundary is what lets the picker sort and label without caring which device it
 * heard from — or whether it heard from one at all.
 */
export const asRegistryDevice = (device: Device): RegistryDevice => {
  const millis = device.lastSeenAt === null ? undefined : toMillis(device.lastSeenAt);
  return {
    id: device.id,
    name: device.name,
    platform: device.platform,
    version: device.version ?? undefined,
    ...(millis !== undefined ? { lastSeenAt: millis } : {})
  };
};

export interface Space {
  id: string;
  deviceId: string;
  path: string;
  name?: string | null;
  createdAt: string;
  /** Owner-stamped: the folder is a git repository (SpacesSync re-verifies). */
  gitDetected?: boolean;
  /** The branch this folder is checked out on, when the device reported one. */
  checkoutId?: string | null;
}

/** What the picker shows for a space: the folder's basename unless renamed. */
export const spaceLabel = (space: Space): string => {
  const named = space.name?.trim();
  if (named !== undefined && named.length > 0) return named;
  const parts = space.path.split("/").filter((part) => part.length > 0);
  return parts[parts.length - 1] ?? space.path;
};

/**
 * A repository is a logical project; a Space is one checkout of that project
 * on one machine. The engine deliberately keeps those identities separate,
 * so the browser groups by the stable human-facing repository name while
 * retaining the concrete Space for routing a new chat.
 */
export interface RepositoryGroup {
  key: string;
  label: string;
  spaces: Space[];
}

export const repositoryKey = (space: Space): string =>
  spaceLabel(space).trim().toLocaleLowerCase();

export const groupSpacesByRepository = (spaces: readonly Space[]): RepositoryGroup[] => {
  const groups = new Map<string, RepositoryGroup>();
  for (const space of spaces) {
    const key = repositoryKey(space);
    const existing = groups.get(key);
    if (existing) {
      existing.spaces.push(space);
    } else {
      groups.set(key, { key, label: spaceLabel(space), spaces: [space] });
    }
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      spaces: [...group.spaces].sort((a, b) => {
        const name = spaceLabel(a).localeCompare(spaceLabel(b));
        if (name !== 0) return name;
        return a.deviceId.localeCompare(b.deviceId);
      })
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export type SessionStatus = "idle" | "working" | "awaitingInput" | "errored";

export interface Session {
  chatId: string;
  deviceId: string;
  status: SessionStatus;
  startedAt: string | null;
  updatedAt: string;
  lastCompletedTurn?: string | null;
}

/** The four user-facing states plus Errored, derived — never stored. */
export type ChatIndicator = "working" | "awaitingInput" | "errored" | "completed" | "idle";

export type MessageRole = "user" | "assistant" | "system";
export type MessageStatus = "streaming" | "complete" | "aborted";

export interface TextPart {
  kind: "text";
  id: string;
  text: string;
}
export interface ReasoningPart {
  kind: "reasoning";
  id: string;
  text: string;
}
export interface ImagePart {
  kind: "image";
  id: string;
  path: string;
  name: string;
  mimeType: string;
}
export interface ToolPart {
  kind: "tool";
  id: string;
  call: { name?: string; title?: string; args?: unknown } | null;
  isError?: boolean;
  resolved?: boolean;
  output?: string | null;
  diff?: { path?: string; additions?: number; deletions?: number }[] | null;
  outputRef?: string | null;
  outputBytes?: number | null;
  diffRef?: string | null;
  diffStats?: { path: string; additions: number; deletions: number }[] | null;
  subagentRef?: string | null;
  subagentStatus?: "running" | "done" | "failed" | null;
  subagentTail?: string | null;
}
export interface InputPart {
  kind: "input";
  id: string;
  requestId: string;
  questions: UserInputQuestion[];
  resolved?: boolean;
}
export interface ErrorPart {
  kind: "error";
  id: string;
  message: string;
}
export interface ForkPart {
  kind: "fork";
  id: string;
  sourceChatId: string;
  sourceTitle: string;
}
/** Unknown part kinds are preserved rather than dropped, so a newer engine's
 * part still occupies its slot and the transcript stays ordered. `kind` is the
 * literal `"unknown"` (not `string`) so the union still narrows properly — the
 * original kind is kept on `rawKind` for diagnostics. */
export interface UnknownPart {
  kind: "unknown";
  id: string;
  rawKind?: string;
}

export type MessagePart =
  | TextPart
  | ReasoningPart
  | ImagePart
  | ToolPart
  | InputPart
  | ErrorPart
  | ForkPart
  | UnknownPart;

export interface TranscriptEntry {
  id: string;
  role: MessageRole;
  parts: MessagePart[];
  createdAt: number;
  deviceId: string;
  status?: MessageStatus | null;
  continuationOf?: string | null;
  durationMs?: number | null;
}

export interface UserInputQuestion {
  id: string;
  header: string;
  question: string;
  options: string[];
  multiSelect?: boolean;
  prefill?: string | null;
  multiline?: boolean;
}

export interface UserInputAnswer {
  questionId: string;
  labels: string[];
}

export interface QueuedMessage {
  id: string;
  text: string;
  attachments?: string[];
  config?: ChatConfig | null;
  holdForTurnEnd?: boolean;
  issuedBy: string;
  issuedAt: number;
  editedAt?: number | null;
  sendAt?: number | null;
  deliveryGate?: unknown;
}

export interface HarnessDescriptor {
  id: string;
  name: string;
  enabled?: boolean;
  installed?: boolean;
  models?: Model[];
}

export interface Model {
  id: string;
  label: string;
  description?: string | null;
  reasoningLevels?: string[];
  options?: ModelOption[];
}

export interface ModelOption {
  id: string;
  label: string;
  choices: { id: string; label: string }[];
  defaultChoice: string;
}

export interface EngineInfo {
  deviceId: string;
  workspaceScope?: string;
  cursorSdkVersion?: string | null;
  capabilities?: string[];
}

/** Epoch millis from either an RFC3339 string or an epoch-millis number. */
export const toMillis = (value: string | number | null | undefined): number | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/** A live session row is only trusted for 45s — the same staleness window the
 * desktop uses, so a crashed engine never shows an eternal "Working". */
export const SESSION_STALENESS_MS = 45_000;

export const liveSession = (
  session: Session | undefined,
  now: number
): Session | undefined => {
  if (session === undefined) return undefined;
  const updated = toMillis(session.updatedAt);
  if (updated === undefined) return undefined;
  return now - updated <= SESSION_STALENESS_MS ? session : undefined;
};

/** True when the chat has activity the user has not seen on any device. */
export const isUnseen = (chat: Chat): boolean => {
  const lastMessage = toMillis(chat.lastMessageAt);
  if (lastMessage === undefined) return false;
  const seen = toMillis(chat.lastSeenAt);
  if (seen === undefined) return true;
  return lastMessage > seen;
};

/** Mirror of `chat_indicator` in `crates/proto/src/entities.rs`. */
export const chatIndicator = (
  chat: Chat,
  live: Session | undefined,
  now: number
): ChatIndicator => {
  const session = liveSession(live, now);
  switch (session?.status) {
    case "working":
      return "working";
    case "awaitingInput":
      return "awaitingInput";
    case "errored":
      return isUnseen(chat) ? "errored" : "idle";
    default:
      return isUnseen(chat) ? "completed" : "idle";
  }
};

/** Unseen, then most recent activity, then creation — the desktop sidebar's
 * order minus the user's own pin/section placement (which the web client does
 * not manage). */
export const sortChats = (chats: readonly Chat[]): Chat[] =>
  [...chats].sort((a, b) => {
    const aAt = toMillis(a.lastMessageAt) ?? toMillis(a.createdAt) ?? 0;
    const bAt = toMillis(b.lastMessageAt) ?? toMillis(b.createdAt) ?? 0;
    if (aAt !== bAt) return bAt - aAt;
    return a.id.localeCompare(b.id);
  });

/** Most recently created first: a project you just added is the one you want. */
export const sortSpaces = (spaces: readonly Space[]): Space[] =>
  [...spaces].sort((a, b) => {
    const aAt = toMillis(a.createdAt) ?? 0;
    const bAt = toMillis(b.createdAt) ?? 0;
    if (aAt !== bAt) return bAt - aAt;
    return a.id.localeCompare(b.id);
  });

export const chatTitle = (chat: Chat): string =>
  chat.title?.trim() || chat.lastMessagePreview?.trim() || "New chat";

/** The pending agent question in a transcript, if any. A transcript can hold
 * several input parts; only the newest unresolved one is answerable. */
export const pendingInput = (
  entries: readonly TranscriptEntry[]
): { entryId: string; part: InputPart } | undefined => {
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const entry = entries[i];
    if (entry === undefined) continue;
    for (const part of entry.parts) {
      if (part.kind === "input" && !(part as InputPart).resolved) {
        return { entryId: entry.id, part: part as InputPart };
      }
    }
  }
  return undefined;
};

/** Human-readable label for a tool call. */
export const toolLabel = (part: ToolPart): string => {
  const call = part.call ?? {};
  const title = typeof call.title === "string" && call.title.length > 0 ? call.title : undefined;
  if (title !== undefined) return title;
  const name = typeof call.name === "string" && call.name.length > 0 ? call.name : "tool";
  const args = call.args as { command?: unknown; path?: unknown } | undefined;
  if (typeof args?.command === "string") return `${name}: ${args.command}`;
  if (typeof args?.path === "string") return `${name}: ${args.path}`;
  return name;
};
